/**
 * EditorState: the observable state shared across the editor subsystems
 * (toolbar, placement, selection, property panel, object list, gizmo).
 *
 * Holds the current transform `mode`, the placement `placementMode`, the
 * `selectedId`, the active left-panel config item (`selectedAssetId`), and a
 * getter for the live object list. Subscribers are notified on any change so
 * the DOM panels can re-render and the 3D gizmo/selection can re-sync. The
 * object list itself is owned by `SceneObjectManager`; this class only reads
 * it and signals when it changes (via `notifyObjectsChanged`).
 */

import type { SceneObject } from '../scene/SceneObjectTypes';

export type EditorMode = 'select' | 'move' | 'rotate' | 'scale' | 'place';
export type PlacementMode = 'raycastSurface' | 'freeDepth';

/** 场景级配置项（配置项目里的出生区/天空盒）：不是可放置对象类型，选中
 *  它们时对象列表无对象，右侧属性面板显示对应的场景配置页。 */
export const SPAWN_AREA_CONFIG_ID = '__spawnAreaConfig__';
export const SKYBOX_CONFIG_ID = '__skyboxConfig__';

/** Whether the id refers to a scene-level config item (出生区/天空盒). */
export function isSceneConfigId(id: string | null): boolean {
    return id === SPAWN_AREA_CONFIG_ID || id === SKYBOX_CONFIG_ID;
}

type Listener = () => void;

export class EditorState {
    mode: EditorMode = 'select';
    placementMode: PlacementMode = 'raycastSurface';
    selectedId: string | null = null;
    /** The left-panel 配置项目 currently chosen (asset id). Clicking it no
     *  longer starts placement directly — it selects the category: the object
     *  list filters to this asset's objectType, and the 新增 button (object
     *  list) starts placement for it. */
    selectedAssetId: string | null = null;
    /** Bumped when the spawn area is created/cleared (shape change only) so the
     *  property panel re-renders its 出生区 page without stealing focus on
     *  every size keystroke. */
    spawnSettingsVersion = 0;

    private _listeners: Listener[] = [];
    private _transformListeners: Listener[] = [];
    private _objectsGetter: () => readonly SceneObject[];

    constructor(objectsGetter: () => readonly SceneObject[]) {
        this._objectsGetter = objectsGetter;
    }

    get objects(): readonly SceneObject[] { return this._objectsGetter(); }

    subscribe(fn: Listener): () => void {
        this._listeners.push(fn);
        return () => { this._listeners = this._listeners.filter((f) => f !== fn); };
    }

    /** Fire listeners. Called after any state mutation. */
    emit(): void {
        for (const f of this._listeners) f();
    }

    /** Subscribe to transform-only changes (gizmo drag / undo / redo). This is
     *  a separate channel from `subscribe` so the property panel can refresh its
     *  input *values* without a full re-render (which would steal focus while
     *  the designer is typing in another field). */
    subscribeTransform(fn: Listener): () => void {
        this._transformListeners.push(fn);
        return () => {
            this._transformListeners = this._transformListeners.filter((f) => f !== fn);
        };
    }

    /** Notify subscribers that the selected object's transform changed (live). */
    notifyTransformChanged(): void {
        for (const f of this._transformListeners) f();
    }

    setMode(m: EditorMode): void {
        if (this.mode === m) return;
        this.mode = m;
        this.emit();
    }

    setPlacementMode(m: PlacementMode): void {
        if (this.placementMode === m) return;
        this.placementMode = m;
        this.emit();
    }

    select(id: string | null): void {
        if (this.selectedId === id) return;
        this.selectedId = id;
        this.emit();
    }

    /** Choose the active left-panel config item (filters the object list). */
    setSelectedAsset(id: string | null): void {
        if (this.selectedAssetId === id) return;
        this.selectedAssetId = id;
        this.emit();
    }

    /** Signal that the object list contents changed (add/delete/marker). */
    notifyObjectsChanged(): void { this.emit(); }
}
